import Button from '@components/Button';
import { Collapse } from '@components/Collapse';
import ColorPicker from '@components/ColorPicker';
import { Dropdown, type DropdownItem } from '@components/Dropdown';
import FeatureToggle from '@components/FeatureToggle';
import Image from '@components/Image';
import ImageContainer from '@components/ImageContainer';
import Slider from '@components/Slider';
import Tabbar from '@components/Tabbar';
import Tip from '@components/Tip';
import { Validator } from '@components/Validator';
import { selectLibSandtableMarkerIcons } from '@modules/mediaLib';
import { $curSandTable } from '@stores/dataStores';
import { $editingImageMarkerApi } from '@stores/editingMarkerStores';
import { $playerWidgetsApi } from '@stores/playerWidgets';
import {
  type GeneralID,
  ImageType,
  type STImageMarkerScene,
  SceneType,
} from '@types';
import { useStoreMap } from 'effector-react';
import produce from 'immer';
import { toFlag, toPercent } from 'pano-utils';
import { useEffect, useRef } from 'react';
import { useUpdateEffect } from 'react-use';
import { stEditorKrpPlayer } from '../STKrpPlayer';
import { BatchSceneList } from '../components/BatchSceneList';
import { SceneSetting } from '../components/SceneSetting';
import { TitleSetting } from '../components/TitleSetting';
import {
  IMAGE_MARKER_TITLE_DEFAULT,
  MARKER_ICON_DEFAULT,
  MAX_MARKERS,
} from '../constants';

interface Props {
  marker: STImageMarkerScene;
  isActive?: boolean;
  batchMode: boolean;
  idList: GeneralID[];
  onIdListChange: (idList: GeneralID[]) => void;
}

const valToString = (val: number) => toPercent(val, 0);

export const markerTypeItems: DropdownItem<
  STImageMarkerScene['icon']['type']
>[] = [
  { id: 0, name: '圆点' },
  { id: 1, name: '图标' },
];

export const markerAnchorItems: DropdownItem<
  NonNullable<STImageMarkerScene['icon']['mediaIcon']>['anchor']
>[] = [
  { id: 'center', name: '正中心' },
  { id: 'topleft', name: '左上角' },
  { id: 'topright', name: '右上角' },
  { id: 'leftbottom', name: '左下角' },
  { id: 'rightbottom', name: '右下角' },
  { id: 'top', name: '顶部居中' },
  { id: 'bottom', name: '底部居中' },
  { id: 'left', name: '左侧居中' },
  { id: 'right', name: '右侧居中' },
];

const DEFAULT_ICON = '/hotspots/system/map/point/a1.png';

export function MarkerSettingsScene({
  marker,
  batchMode,
  isActive = false,
  idList,
  onIdListChange,
}: Props) {
  const { icon, data, showTitle, title, titleSetting } = marker;
  const prevIconTypeRef = useRef<STImageMarkerScene['icon']['type']>(icon.type);
  const markersCount = useStoreMap({
    store: $curSandTable,
    keys: [],
    fn: (state) => state?.markers?.length ?? 0,
  });

  const markerType = icon.type ?? 0;

  useUpdateEffect(() => {
    if (batchMode) return;
    if (prevIconTypeRef.current !== icon.type) {
      const { ath, atv } = stEditorKrpPlayer.getEditingPos();
      stEditorKrpPlayer.startEditing(
        produce(marker, (draft) => {
          draft.icon.ath = ath;
          draft.icon.atv = atv;
        })
      );
      prevIconTypeRef.current = icon.type;
    } else {
      stEditorKrpPlayer.updateIcon({ icon, isActive });
    }
  }, [icon, isActive]);

  useUpdateEffect(() => {
    if (batchMode) return;
    stEditorKrpPlayer.updateTitle(marker);
  }, [title, titleSetting, showTitle]);

  useEffect(() => {
    if (batchMode) return;
    stEditorKrpPlayer.toggleRadar(
      !!icon.showRadar && data.sceneType === SceneType.pano
    );
  }, [icon.showRadar, data.sceneType]);

  useEffect(() => {
    if (idList.length > 0) {
      $playerWidgetsApi.add('batchPreview');
    } else {
      $playerWidgetsApi.clear();
    }
  }, [!!idList.length]);

  const handleSelectImages = async () => {
    const { libItems, sysItems } = await selectLibSandtableMarkerIcons();
    if (libItems) {
      $editingImageMarkerApi.update((draft) => {
        draft.icon.mediaIcon = {
          type: 1,
          id: libItems[0].id,
          url: libItems[0].url,
          anchor: draft.icon.mediaIcon?.anchor ?? 'bottom',
        };
      });
    } else if (sysItems) {
      $editingImageMarkerApi.update((draft) => {
        draft.icon.mediaIcon = {
          type: 0,
          id: 0,
          url: sysItems[0].url,
          anchor: draft.icon.mediaIcon?.anchor ?? 'bottom',
        };
      });
    }
  };

  return (
    <>
      {batchMode ? (
        <Validator
          value={idList}
          validFn={(value) => {
            return (
              value.length + markersCount <= MAX_MARKERS && value.length > 0
            );
          }}
          warningMessage={
            idList.length
              ? '标记点数超过上限，请删除部分场景后再添加'
              : '请选择标记场景'
          }
        >
          {({ value, warning }) => (
            <BatchSceneList
              idList={value}
              warning={warning}
              onChange={onIdListChange}
            />
          )}
        </Validator>
      ) : (
        <SceneSetting
          data={data}
          onAngleSet={(val) => {
            stEditorKrpPlayer.updateAngle(val);
          }}
          onSelect={(sceneData) => {
            $editingImageMarkerApi.update((draft) => {
              draft.data.sceneId = sceneData.id;
              draft.data.sceneType = sceneData.type;
              draft.title = sceneData.name;
            });
          }}
        />
      )}
      {(!batchMode || idList.length > 0) && (
        <>
          <Collapse title="标记图标">
            <Dropdown
              items={markerTypeItems}
              selected={markerType}
              className="mb-20"
              onChange={(val) => {
                $editingImageMarkerApi.update((draft) => {
                  draft.icon.type = val;
                  if (val === 1) {
                    draft.icon.mediaIcon = {
                      type: 0,
                      id: 0,
                      url: DEFAULT_ICON,
                      anchor: 'bottom',
                    };
                    draft.titleSetting.oy = -40;
                    draft.titleSetting.ox = IMAGE_MARKER_TITLE_DEFAULT.ox;
                    draft.titleSetting.edge = IMAGE_MARKER_TITLE_DEFAULT.edge;
                  } else {
                    draft.icon.mediaIcon = undefined;
                    draft.titleSetting.oy = IMAGE_MARKER_TITLE_DEFAULT.oy;
                    draft.titleSetting.ox = IMAGE_MARKER_TITLE_DEFAULT.ox;
                    draft.titleSetting.edge = IMAGE_MARKER_TITLE_DEFAULT.edge;
                  }
                });
              }}
            />
            {markerType === 0 && (
              <>
                <Tabbar
                  tabs={['默认状态', '选中状态']}
                  activeIndex={isActive ? 1 : 0}
                  onChange={(index) => {
                    $editingImageMarkerApi.setIsActive(index === 1);
                  }}
                />
                <ColorPicker
                  className="mt-20"
                  colorString={isActive ? icon.activeFillColor : icon.fillColor}
                  onChange={(val) => {
                    if (isActive) {
                      $editingImageMarkerApi.update((draft) => {
                        draft.icon.activeFillColor = val;
                      });
                    } else {
                      $editingImageMarkerApi.update((draft) => {
                        draft.icon.fillColor = val;
                      });
                    }
                  }}
                >
                  <ColorPicker.Header>
                    <div>填充颜色：</div>
                    <ColorPicker.Reset
                      defaultColor={
                        isActive
                          ? MARKER_ICON_DEFAULT.activeFillColor
                          : MARKER_ICON_DEFAULT.fillColor
                      }
                    />
                  </ColorPicker.Header>
                  <ColorPicker.Bar />
                </ColorPicker>
                <ColorPicker
                  className="mt-20"
                  colorString={
                    isActive ? icon.activeBorderColor : icon.borderColor
                  }
                  onChange={(val) => {
                    if (isActive) {
                      $editingImageMarkerApi.update((draft) => {
                        draft.icon.activeBorderColor = val;
                      });
                    } else {
                      $editingImageMarkerApi.update((draft) => {
                        draft.icon.borderColor = val;
                      });
                    }
                  }}
                >
                  <ColorPicker.Header>
                    <div>边框颜色：</div>
                    <ColorPicker.Reset
                      defaultColor={
                        isActive
                          ? MARKER_ICON_DEFAULT.activeBorderColor
                          : MARKER_ICON_DEFAULT.borderColor
                      }
                    />
                  </ColorPicker.Header>
                  <ColorPicker.Bar />
                </ColorPicker>
              </>
            )}
            {markerType === 1 && (
              <>
                <ImageContainer
                  className="mt-10 h-70"
                  onDeleteClick={() => {
                    $editingImageMarkerApi.update((draft) => {
                      if (draft.icon.mediaIcon) {
                        draft.icon.mediaIcon.id = 0;
                        draft.icon.mediaIcon.type = 0;
                        draft.icon.mediaIcon.url = DEFAULT_ICON;
                      }
                    });
                  }}
                >
                  {icon.mediaIcon && (
                    <Image
                      src={icon.mediaIcon.url}
                      type={
                        icon.mediaIcon.type
                          ? ImageType.media
                          : ImageType.sysMedia
                      }
                      className="max-w-40 max-h-40"
                    />
                  )}
                </ImageContainer>
                <div className="mt-10 flex items-center justify-between">
                  <Tip>建议尺寸:100*100</Tip>
                  <Button size="sm" onClick={handleSelectImages}>
                    选择图片
                  </Button>
                </div>
              </>
            )}
            <Slider
              className="mt-20"
              min={0.5}
              max={2}
              value={icon.scaleX}
              valToString={valToString}
              onChange={(val) => {
                $editingImageMarkerApi.update((draft) => {
                  draft.icon.scaleX = val;
                  draft.icon.scaleY = val;
                });
                stEditorKrpPlayer.updateRadarScale(val);
              }}
            >
              <Slider.Header title="图标缩放：" />
              <Slider.Bar step={0.01} />
              <Slider.Control step={0.01} />
            </Slider>
            {(data.sceneType === SceneType.pano || batchMode) && (
              <FeatureToggle
                title="显示雷达方向"
                isOn={!!icon.showRadar}
                onToggle={(bool) => {
                  $editingImageMarkerApi.update((draft) => {
                    draft.icon.showRadar = toFlag(bool);
                  });
                }}
              />
            )}
            {markerType === 1 && (
              <>
                <div className="mt-20">图标锚点</div>
                <Dropdown
                  className="mt-10"
                  items={markerAnchorItems}
                  selected={icon.mediaIcon?.anchor ?? 'bottom'}
                  onChange={(val) => {
                    $editingImageMarkerApi.update((draft) => {
                      if (draft.icon.mediaIcon) {
                        draft.icon.mediaIcon.anchor = val;
                      }
                    });
                  }}
                />
              </>
            )}
          </Collapse>
          <TitleSetting
            title={title}
            showTitle={showTitle}
            onTitleChange={
              batchMode
                ? undefined
                : (val) => {
                    $editingImageMarkerApi.update((draft) => {
                      draft.title = val;
                    });
                  }
            }
            onToggleChange={(val) => {
              $editingImageMarkerApi.update((draft) => {
                draft.showTitle = val;
              });
            }}
          />
        </>
      )}
    </>
  );
}
